import React, { useCallback } from 'react';
import { useIsFocused, useNavigation } from '@react-navigation/native';
import { useInaccessibleElement } from '@sg/garnish';

import { Navbar as NavbarComponents } from '@order/navigation';
import { useIsExpoRouterEnabled } from '@order/shared/hooks';
import { useTelemetry } from '@order/Telemetry';

import { useNavigateToLastInteractedStore } from '../../context/LastInteractedStore';
import { useBagCount, useLoyaltyAvailablePoints } from '../../hooks';
import { useSafeRouter } from '../../navigation/hooks';

export const Navbar = () => {
  const isExpoRouterEnabled = useIsExpoRouterEnabled();

  const { track } = useTelemetry();
  const { navigate } = useNavigation();
  const router = useSafeRouter();
  const navigateLastStore = useNavigateToLastInteractedStore();

  // ─── Accessibility ─────────────────────────────────────────────────────────
  // Here we add the "accessibilityHidden" prop when the navbar isn't focused.
  // This is because with react-navigation we'll render this navbar multiple times.

  const isVisible = useIsFocused();
  const a11yProps = useInaccessibleElement(!isVisible);

  // ─── Remote Data ───────────────────────────────────────────────────────────

  const points = useLoyaltyAvailablePoints();
  const { bagCount } = useBagCount();

  // ─── Callbacks ─────────────────────────────────────────────────────────────

  const navigateHome = useCallback(() => {
    track('home.jump_nav');

    if (isExpoRouterEnabled) {
      router?.navigate('/');
      return;
    }

    navigate('MainTabs', { screen: 'HomeTab', params: { screen: 'Home' } });
  }, [track, isExpoRouterEnabled, router, navigate]);

  const navigateLoyalty = useCallback(() => {
    track('loyalty.jump_nav');

    if (isExpoRouterEnabled) {
      router?.navigate('/sg-rewards');
      return;
    }

    navigate('MainTabs', { screen: 'LoyaltyTab', params: { screen: 'LoyaltyHome' } }); // prettier-ignore
  }, [track, isExpoRouterEnabled, router, navigate]);

  const navigateGift = useCallback(() => {
    track('gift.jump_nav');

    if (isExpoRouterEnabled) {
      router?.navigate('/gift');
      return;
    }

    navigate('MainTabs', { screen: 'GiftCardsTab', params: { screen: 'GiftCards' } }); // prettier-ignore
  }, [track, isExpoRouterEnabled, router, navigate]);

  const navigateMenu = useCallback(() => {
    track('menu.jump_nav', {});
    navigateLastStore();
  }, [track, navigateLastStore]);

  const navigateOrder = useCallback(() => {
    track('order.jump_nav');
    navigateLastStore();
  }, [track, navigateLastStore]);

  const navigateBag = useCallback(() => {
    track('bag.jump_nav');

    if (isExpoRouterEnabled) {
      router?.navigate('/bag');
      return;
    }

    navigate('Modal', { screen: 'Bag' });
  }, [track, isExpoRouterEnabled, router, navigate]);

  const navigateAccount = useCallback(() => {
    track('account.jump_nav');

    if (isExpoRouterEnabled) {
      router?.navigate('/account/menu');
      return;
    }

    navigate('MainTabs', { screen: 'AccountTab', params: undefined as never }); // prettier-ignore
  }, [track, isExpoRouterEnabled, router, navigate]);

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <NavbarComponents.Containers.Main {...a11yProps}>
      <NavbarComponents.Containers.Left>
        <NavbarComponents.Gift onPress={navigateGift} />
        <NavbarComponents.Loyalty points={points} onPress={navigateLoyalty} />
        <NavbarComponents.Menu onPress={navigateMenu} />
      </NavbarComponents.Containers.Left>

      <NavbarComponents.Home onPress={navigateHome} />

      <NavbarComponents.Containers.Right>
        <NavbarComponents.Order onPress={navigateOrder} />
        <NavbarComponents.Bag count={bagCount} onPress={navigateBag} />
        <NavbarComponents.Account onPress={navigateAccount} />
      </NavbarComponents.Containers.Right>
    </NavbarComponents.Containers.Main>
  );
};
